import {
    FormConfig,
    FormErrorsType,
    TextFieldConfig,
    useErrorTranslations,
    useForm,
} from '@/hooks/useForm'
import { Alert, Spinner, TextField } from '@/components'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import useToastStore from '@/stores/useToastStore/useToastStore'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import { useGlobalModalStore } from '@/stores/useGlobalModalStore/useGlobalModalStore'
import { ReportType, SaveReportRequestBody } from '@/hooks'
import { useBillingFiltersSaveParams } from '@/features/BillingPayments/hooks/useBillingFiltersSaveParams'
import { useAnalyticsFiltersSaveParams } from '@/features/Analytics/hooks/useAnalyticsFiltersSaveParams'
import { useBenchmarkingFiltersSaveParams } from '@/features/Analytics/hooks/useBenchmarkingFiltersSaveParams'
import useSustainabilityStore from '@/features/Sustainability/stores/useSustainabilityStore/useSustainabilityStore'
import { useSustainabilityFiltersSaveParams } from '@/features/Sustainability/hooks/useSustainabilityFiltersSaveParams'
import { useActiveManagementFiltersSaveParams } from '@/features/ActiveManagement/hooks/useActiveManagementFiltersSaveParams'
import useActiveManagementStore from '@/features/ActiveManagement/stores/useActiveManagementStore/useActiveManagementStore'

type SaveReportContentProps = {
    reportType: ReportType | 'electricity-mix'
    labels: {
        saveButton: string
        loadingSaveButton: string
    }
    handleOnSuccess?: () => void
    onPendingSave?: (isPending: boolean) => void
    report?: Omit<SaveReportRequestBody, 'name'> & { name?: string }
}

export const SaveReportContent: React.FC<SaveReportContentProps> = ({
    reportType,
    labels,
    handleOnSuccess,
    report,
    onPendingSave,
}) => {
    const { t } = useTranslation('translations')

    const reportName = report?.name ?? ''

    const formConfig: FormConfig = useMemo(
        () => ({
            reportName: {
                type: 'text' as TextFieldConfig['type'],
                label: t('reporting.save_report.input_label'),
                placeholder: t('reporting.save_report.input_placeholder'),
                value: reportName,
                required: true,
            },
        }),
        [t, reportName]
    )

    const {
        getInputProps,
        setFieldValue,
        formState,
        isValid,
        setAllFieldsDirty,
    } = useForm(formConfig)

    const addAlert = useToastStore((state) => state.addAlert)
    const { useSaveReportMutation } = useReportsApi()
    const { mutate: saveReport, isPending, reset } = useSaveReportMutation()

    useEffect(() => {
        onPendingSave?.(isPending)
    }, [isPending, onPendingSave])

    const dataSets = useAnalyticsStore((state) => state.usageAndCost.dataSets)
    const analyticsFiltersParams = useAnalyticsFiltersSaveParams()
    const billingFiltersParams = useBillingFiltersSaveParams()
    const sustainabilityFiltersParams = useSustainabilityFiltersSaveParams()
    const benchmarkingFiltersParams = useBenchmarkingFiltersSaveParams()
    const intervalFiltersParams = useActiveManagementFiltersSaveParams()
    const benchmarkingReportSettings = useAnalyticsStore(
        (state) => state.benchmarking.reportSettings
    )
    const totalEmissionsReportSettings = useSustainabilityStore(
        (state) => state.totalEmissions.reportSettings
    )
    const electricityMixReportSettings = useSustainabilityStore(
        (state) => state.electricityMix.reportSettings
    )
    const ghgEmissionsReportSettings = useSustainabilityStore(
        (state) => state.ghgEmissions.reportSettings
    )
    const intervalReportSettings = useActiveManagementStore(
        (state) => state.intervalData.reportSettings
    )

    const actualReportType: ReportType =
        reportType === 'electricity-mix' ? 'total-emissions' : reportType

    const setModalState = useGlobalModalStore((state) => state.setModalState)
    const [errors, setErrors] = useState<FormErrorsType>({})

    const closeModal = () => {
        setModalState({ isOpen: false })
    }
    const fields = actualReportType === 'usage-cost' ? dataSets : []

    const filtersParams = useMemo(() => {
        if (actualReportType === 'usage-cost') {
            return analyticsFiltersParams
        }
        if (actualReportType === 'payables-summary') {
            return billingFiltersParams
        }
        if (actualReportType === 'total-emissions') {
            return sustainabilityFiltersParams
        }
        if (actualReportType === 'ghg-emissions') {
            return sustainabilityFiltersParams
        }
        if (actualReportType === 'interval-data') {
            return intervalFiltersParams
        }
        return benchmarkingFiltersParams
    }, [
        actualReportType,
        benchmarkingFiltersParams,
        analyticsFiltersParams,
        billingFiltersParams,
        sustainabilityFiltersParams,
        intervalFiltersParams,
    ])

    const submitText = isPending ? labels.loadingSaveButton : labels.saveButton
    const dismissText = t('reporting.save_report.cancel_button')
    const textFieldProps = getInputProps('reportName')

    const handleChange = (value: string) => {
        setFieldValue('reportName', value)
        setErrors({})
    }

    const errorString = useErrorTranslations()

    const mergedErrors = useMemo(() => {
        if (!textFieldProps.errors) {
            return errors
        }
        return {
            ...errors,
            ...textFieldProps.errors,
        }
    }, [errors, textFieldProps.errors])

    const onSubmit = (inputValue: string) => {
        if (!isValid) {
            setAllFieldsDirty()
            return
        }

        const saveReportPayload = (() => {
            if (report) {
                return {
                    ...report,
                    name: inputValue,
                }
            }

            const payload = {
                name: inputValue,
                filters: filtersParams,
                fields: fields,
            }

            if (reportType === 'benchmarking') {
                return {
                    ...payload,
                    type: 'benchmarking' as const,
                    settings: {
                        basis: benchmarkingReportSettings.basis,
                    },
                }
            }

            if (reportType === 'total-emissions') {
                return {
                    ...payload,
                    type: 'total-emissions' as const,
                    settings: {
                        type: totalEmissionsReportSettings.type,
                        scope: totalEmissionsReportSettings.scope,
                        scope2type: totalEmissionsReportSettings.scope2type,
                    },
                }
            }

            if (reportType === 'electricity-mix') {
                return {
                    ...payload,
                    type: 'total-emissions' as const,
                    settings: {
                        type: electricityMixReportSettings.type,
                        scope: electricityMixReportSettings.scope,
                        scope2type: electricityMixReportSettings.scope2type,
                    },
                }
            }

            if (reportType === 'ghg-emissions') {
                return {
                    ...payload,
                    type: 'ghg-emissions' as const,
                    settings: {
                        scope: ghgEmissionsReportSettings.scope,
                        scope2type: ghgEmissionsReportSettings.scope2type,
                        showTotals: ghgEmissionsReportSettings.showTotals,
                    },
                }
            }

            if (reportType === 'interval-data') {
                const dataType: 'subMeterData' | 'utilityData' =
                    intervalReportSettings.display === 'subMeters'
                        ? 'subMeterData'
                        : 'utilityData'

                return {
                    ...payload,
                    type: 'interval-data' as const,
                    settings: {
                        dataGrouping: intervalReportSettings.dataGrouping,
                        timeInterval: intervalReportSettings.timeInterval,
                        timezone: intervalReportSettings.timezone,
                        dataType: intervalReportSettings.dataType,
                        type: dataType,
                        showHighlights: intervalReportSettings.showHighlights,
                        granularity: intervalReportSettings.granularity,
                    },
                }
            }

            return {
                ...payload,
                type: reportType,
            }
        })()

        saveReport(saveReportPayload, {
            onSuccess: () => {
                closeModal()
                addAlert({
                    variant: 'success',
                    title: t('reporting.save_report.success_toast.title'),
                    description: t(
                        'reporting.save_report.success_toast.description',
                        {
                            reportName: inputValue,
                            link: '<a href="/reporting">Reporting</a>',
                        }
                    ),
                })
                handleOnSuccess?.()
                onPendingSave?.(false)
            },
            onError: (error) => {
                const data = error.response?.data
                if (
                    typeof data === 'object' &&
                    data !== null &&
                    'message' in data &&
                    typeof data.message === 'string' &&
                    data.message === 'Conflict'
                ) {
                    setErrors({
                        ...errors,
                        reportName: errorString['ERR_DUPLICATE_REPORT_NAME'],
                    })
                    reset()
                    onPendingSave?.(false)
                    return
                }
                onPendingSave?.(false)
                closeModal()
                addAlert({
                    variant: 'error',
                    title: t('reporting.save_report.error_toast.title'),
                    description: t(
                        'reporting.save_report.error_toast.description'
                    ),
                })
                reset()
            },
        })
    }

    return (
        <div className="text-sm">
            <div className="space-y-6 px-6 pb-6">
                <TextField
                    {...getInputProps('reportName')}
                    onChange={handleChange}
                    disabled={isPending}
                    errors={mergedErrors}
                />
                <Alert
                    variant="info"
                    description={t('reporting.save_report.alert')}
                />
            </div>
            <div className="flex flex-row-reverse gap-4 border-t border-t-neutral-200 px-6 py-4">
                <Button
                    name="submit"
                    variant={'solidPrimary'}
                    size="large"
                    onClick={() => {
                        onSubmit(formState['reportName'].toString())
                    }}
                    disabled={isPending}
                    className="whitespace-nowrap"
                >
                    {submitText}
                    {isPending && (
                        <span data-testid="loading-state">
                            <Spinner size={16} width={3} />
                        </span>
                    )}
                </Button>
                <Button
                    name="cancel"
                    variant={'ghostSecondary'}
                    size="large"
                    onClick={closeModal}
                    className="whitespace-nowrap"
                >
                    {dismissText}
                </Button>
            </div>
        </div>
    )
}
